import { useState } from "react";
import { Button } from "newui/components/Button";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { useStartCompliance } from "newui/features/Rfx/Evaluation/hooks/useCompliance";

export interface SkippedComplianceSummaryProps {
	rfxId: number;
}

/**
 * Shown on Step 0 when the lead has SKIPPED the compliance check. Rather than
 * re-presenting the (auto-evaluated) grid, we surface that the step was skipped
 * and offer "Start Compliance Check", which re-opens Pre-Compliance and clears
 * the blanket evaluated/compliant decision so the grid starts from a clean slate.
 */
export const SkippedComplianceSummary = ({
	rfxId,
}: SkippedComplianceSummaryProps) => {
	const startMutation = useStartCompliance(rfxId);
	const [error, setError] = useState<string | null>(null);

	const handleStart = () => {
		setError(null);
		// On success the hook invalidates the compliance cache; status flips off
		// "skipped" and this component is replaced by the questions setup (Stage 1).
		startMutation.mutate(undefined, {
			onError: () =>
				setError(
					"We couldn't re-open the compliance check. Please try again."
				),
		});
	};

	return (
		<div data-testid="pre-compliance-skipped">
			<InstructionsInfoBox inline data-testid="pre-compliance-skipped-info">
				The compliance check was skipped for this RFx — all submissions were
				marked compliant and included in the evaluation. You can start the
				compliance check to assess each supplier against compliance questions;
				doing so clears the current &ldquo;compliant&rdquo; decisions.
			</InstructionsInfoBox>

			{error && (
				<p
					className="mt-4 text-sm text-red-600"
					data-testid="pre-compliance-start-error"
				>
					{error}
				</p>
			)}

			<div className="mt-6">
				<Button
					btntype="primary"
					isLoading={startMutation.isPending}
					disabled={startMutation.isPending}
					onClick={handleStart}
					data-testid="pre-compliance-start-btn"
				>
					Start Compliance Check
				</Button>
			</div>
		</div>
	);
};
